import React, { useState } from 'react';
import { Building2, Globe, DollarSign, Users, AlertTriangle, X } from 'lucide-react';
import { colors } from '../../config/colors';
import { Button } from '../UI/button';
import { Dialog, DialogContent } from '../UI/dialog';
import { useLocale } from '../../contexts/LocaleContext';

const CreateCompanyModal = ({ onClose, onSubmit }) => {
  const { t } = useLocale();
  const [formData, setFormData] = useState({
    ticker: '',
    name: '',
    description: '',
    industry: '',
    tier: '1',
    sector: '',
    country: '',
    website: '',
    marketCap: '',
    employeeCount: '',
    keywords: [],
    supplyChainRisks: [],
    riskCategories: []
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [riskInput, setRiskInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const industries = [
    { value: 'Technology', labelKey: 'industryTechnology' },
    { value: 'Healthcare', labelKey: 'industryHealthcare' },
    { value: 'Finance', labelKey: 'industryFinance' },
    { value: 'Manufacturing', labelKey: 'industryManufacturing' },
    { value: 'Retail', labelKey: 'industryRetail' },
    { value: 'Energy', labelKey: 'industryEnergy' },
    { value: 'Transportation', labelKey: 'industryTransportation' },
    { value: 'Automotive', labelKey: 'industryAutomotive' },
    { value: 'Pharmaceuticals', labelKey: 'industryPharmaceuticals' },
    { value: 'Telecommunications', labelKey: 'industryTelecommunications' },
    { value: 'Aerospace', labelKey: 'industryAerospace' },
    { value: 'Consumer Goods', labelKey: 'industryConsumerGoods' },
    { value: 'Food & Beverage', labelKey: 'industryFoodBeverage' },
    { value: 'Other', labelKey: 'industryOther' },
  ];

  const commonRiskCategories = [
    { value: 'Geopolitical Risk', labelKey: 'riskCategoryGeopolitical' },
    { value: 'Supply Chain Disruption', labelKey: 'riskCategorySupplyChainDisruption' },
    { value: 'Technology Risk', labelKey: 'riskCategoryTechnology' },
    { value: 'Cybersecurity Risk', labelKey: 'riskCategoryCybersecurity' },
    { value: 'Market Competition Risk', labelKey: 'riskCategoryMarketCompetition' },
    { value: 'Regulatory Risk', labelKey: 'riskCategoryRegulatory' },
    { value: 'Financial Risk', labelKey: 'riskCategoryFinancial' },
    { value: 'Operational Risk', labelKey: 'riskCategoryOperational' },
    { value: 'Environmental Risk', labelKey: 'riskCategoryEnvironmental' },
    { value: 'Reputational Risk', labelKey: 'riskCategoryReputational' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const addRisk = () => {
    if (riskInput.trim() && !formData.supplyChainRisks.includes(riskInput.trim())) {
      setFormData(prev => ({
        ...prev,
        supplyChainRisks: [...prev.supplyChainRisks, riskInput.trim()]
      }));
      setRiskInput('');
    }
  };

  const removeRisk = (risk) => {
    setFormData(prev => ({
      ...prev,
      supplyChainRisks: prev.supplyChainRisks.filter(r => r !== risk)
    }));
  };

  const toggleRiskCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      riskCategories: prev.riskCategories.includes(category)
        ? prev.riskCategories.filter(c => c !== category)
        : [...prev.riskCategories, category]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.ticker.trim()) {
      newErrors.ticker = t('createSupplierTickerRequired');
    } else if (formData.ticker.length > 32) {
      newErrors.ticker = t('createSupplierTickerMaxLength', { max: 32 });
    }

    if (!formData.name.trim()) {
      newErrors.name = t('createSupplierNameRequired');
    } else if (formData.name.length > 255) {
      newErrors.name = t('createSupplierNameMaxLength', { max: 255 });
    }

    if (!formData.description.trim()) {
      newErrors.description = t('createSupplierDescriptionRequired');
    } else if (formData.description.length > 1000) {
      newErrors.description = t('createSupplierDescriptionMaxLength', { max: 1000 });
    }

    if (!formData.industry.trim()) {
      newErrors.industry = t('createSupplierIndustryRequired');
    }

    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website = t('createSupplierWebsiteInvalid');
    }

    if (formData.marketCap && isNaN(parseFloat(formData.marketCap))) {
      newErrors.marketCap = t('createSupplierMarketCapNumber');
    }

    if (formData.employeeCount && isNaN(parseInt(formData.employeeCount))) {
      newErrors.employeeCount = t('createSupplierEmployeeCountNumber');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Format data for API (CompanyDTO shape: camelCase)
      const payload = {
        ticker: formData.ticker.trim().toUpperCase(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        industry: formData.industry.trim(),
        tier: formData.tier,
        keywords: formData.keywords,
        supplyChainRisks: formData.supplyChainRisks,
        riskCategories: formData.riskCategories,
        sector: formData.sector.trim() || undefined,
        country: formData.country.trim() || undefined,
        website: formData.website.trim() || undefined,
        marketCap: formData.marketCap ? parseFloat(formData.marketCap) : undefined,
        employeeCount: formData.employeeCount ? parseInt(formData.employeeCount, 10) : undefined
      };

      await onSubmit(payload);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto gap-0 p-0 border-t-4 border-t-primary-500"
        closeLabel={t('dialogCloseLabel')}
      >
          {/* Header */}
          <div className="sticky top-0 z-10 border-b border-border-light bg-background-card px-8 py-6 pr-14">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100">
                <Building2 className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h2 id="create-company-title" className="text-2xl font-bold text-text-primary">
                  {t('createSupplierTitle')}
                </h2>
                <p className="text-sm text-text-secondary">{t('createSupplierSubtitle')}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text.primary }}>
                {t('createSupplierSectionBasic')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ticker */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    {t('createSupplierTickerLabel')} *
                  </label>
                  <input
                    type="text"
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleChange}
                    maxLength={32}
                    placeholder={t('createSupplierTickerPlaceholder')}
                    className={`input ${errors.ticker ? 'border-red-500' : ''}`}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {errors.ticker && (
                    <p className="mt-1 text-sm text-red-600">{errors.ticker}</p>
                  )}
                </div>

                {/* Supplier Name */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    {t('createSupplierNameLabel')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    maxLength={255}
                    placeholder={t('createSupplierNamePlaceholder')}
                    className={`input ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    {t('createSupplierIndustryLabel')} *
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className={`input ${errors.industry ? 'border-red-500' : ''}`}
                  >
                    <option value="">{t('createSupplierIndustryPlaceholder')}</option>
                    {industries.map((ind) => (
                      <option key={ind.value} value={ind.value}>
                        {t(ind.labelKey)}
                      </option>
                    ))}
                  </select>
                  {errors.industry && (
                    <p className="mt-1 text-sm text-red-600">{errors.industry}</p>
                  )}
                </div>

                {/* Sector */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    {t('createSupplierTierLabel')} *
                  </label>
                  <select
                    name="tier"
                    value={formData.tier}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="1">{t('createSupplierTier1')}</option>
                    <option value="2">{t('createSupplierTier2')}</option>
                    <option value="3">{t('createSupplierTier3')}</option>
                  </select>
                </div>

                {/* Sector */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    {t('createSupplierSectorLabel')}
                  </label>
                  <input
                    type="text"
                    name="sector"
                    value={formData.sector}
                    onChange={handleChange}
                    placeholder={t('createSupplierSectorPlaceholder')}
                    className="input"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    {t('createSupplierCountryLabel')}
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder={t('createSupplierCountryPlaceholder')}
                    className="input"
                  />
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    <Globe className="inline h-4 w-4 mr-1" />
                    {t('createSupplierWebsiteLabel')}
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder={t('createSupplierWebsitePlaceholder')}
                    className={`input ${errors.website ? 'border-red-500' : ''}`}
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                  )}
                </div>

                {/* Market Cap */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    <DollarSign className="inline h-4 w-4 mr-1" />
                    {t('createSupplierMarketCapLabel')}
                  </label>
                  <input
                    type="number"
                    name="marketCap"
                    value={formData.marketCap}
                    onChange={handleChange}
                    placeholder={t('createSupplierMarketCapPlaceholder')}
                    className={`input ${errors.marketCap ? 'border-red-500' : ''}`}
                  />
                  {errors.marketCap && (
                    <p className="mt-1 text-sm text-red-600">{errors.marketCap}</p>
                  )}
                </div>

                {/* Employee Count */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                    <Users className="inline h-4 w-4 mr-1" />
                    {t('createSupplierEmployeeCountLabel')}
                  </label>
                  <input
                    type="number"
                    name="employeeCount"
                    value={formData.employeeCount}
                    onChange={handleChange}
                    placeholder={t('createSupplierEmployeeCountPlaceholder')}
                    className={`input ${errors.employeeCount ? 'border-red-500' : ''}`}
                  />
                  {errors.employeeCount && (
                    <p className="mt-1 text-sm text-red-600">{errors.employeeCount}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                {t('createSupplierDescriptionLabel')} *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={1000}
                rows={4}
                placeholder={t('createSupplierDescriptionPlaceholder')}
                className={`input ${errors.description ? 'border-red-500' : ''}`}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.description && (
                  <p className="text-sm text-red-600">{errors.description}</p>
                )}
                <p className="text-xs text-gray-400 ml-auto">
                  {formData.description.length}/1000
                </p>
              </div>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                {t('createSupplierKeywordsLabel')}
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addKeyword();
                    }
                  }}
                  placeholder={t('createSupplierKeywordsPlaceholder')}
                  className="input flex-1 rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: colors.border.light }}
                />
                <Button type="button" variant="secondary" size="default" onClick={addKeyword} className="shrink-0">
                  {t('createSupplierAddButton')}
                </Button>
              </div>
              {formData.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                      style={{ 
                        backgroundColor: colors.primary[100], 
                        color: colors.primary[700] 
                      }}
                    >
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(keyword)}
                        className="ml-2 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Risk Categories */}
            <div>
              <label className="block text-sm font-medium mb-3" style={{ color: colors.text.secondary }}>
                <AlertTriangle className="inline h-4 w-4 mr-1" />
                {t('createSupplierRiskCategoriesLabel')}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {commonRiskCategories.map((category) => (
                  <label
                    key={category.value}
                    className="flex items-center space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all"
                    style={{
                      borderColor: formData.riskCategories.includes(category.value)
                        ? colors.primary[500]
                        : colors.border.light,
                      backgroundColor: formData.riskCategories.includes(category.value)
                        ? colors.primary[50]
                        : 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.riskCategories.includes(category.value)}
                      onChange={() => toggleRiskCategory(category.value)}
                      className="rounded"
                      style={{ accentColor: colors.primary[500] }}
                    />
                    <span className="text-sm font-medium" style={{ color: colors.text.secondary }}>
                      {t(category.labelKey)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Supply Chain Risks */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                {t('createSupplierSupplyChainRisksLabel')}
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={riskInput}
                  onChange={(e) => setRiskInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addRisk();
                    }
                  }}
                  placeholder={t('createSupplierSupplyChainRisksPlaceholder')}
                  className="input flex-1 rounded-lg border px-3 py-2 text-sm"
                  style={{ borderColor: colors.border.light }}
                />
                <Button type="button" variant="secondary" size="default" onClick={addRisk} className="shrink-0">
                  {t('createSupplierAddButton')}
                </Button>
              </div>
              {formData.supplyChainRisks.length > 0 && (
                <div className="space-y-2">
                  {formData.supplyChainRisks.map((risk, index) => (
                    <div
                      key={index}
                      className="flex items-start justify-between p-3 rounded-lg"
                      style={{ backgroundColor: colors.warning[50] }}
                    >
                      <span className="text-sm" style={{ color: colors.text.secondary }}>
                        {risk}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeRisk(risk)}
                        className="text-red-600 hover:text-red-700 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t" style={{ borderColor: colors.border.light }}>
              <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block mr-2" />
                    {t('createSupplierCreating')}
                  </>
                ) : (
                  <>
                    <Building2 className="h-4 w-4 mr-2" />
                    {t('createSupplierSubmit')}
                  </>
                )}
              </Button>
            </div>
          </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCompanyModal;


